import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Copy, RefreshCw, Users } from 'lucide-react';
import { childConnectionService } from '@/services/childConnectionService';
import { useToast } from '@/hooks/use-toast';
import { normalizeAgeGroup } from '@/utils/ageUtils';

interface ChildConnectionCodeProps {
  userId: string;
  userAgeGroup: string;
}

const ChildConnectionCode: React.FC<ChildConnectionCodeProps> = ({ userId, userAgeGroup }) => {
  const [connectionCode, setConnectionCode] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [regenerating, setRegenerating] = useState(false);
  const { toast } = useToast();

  // Only show for under 13 users
  const normalizedAgeGroup = normalizeAgeGroup(userAgeGroup as any);
  console.log('ChildConnectionCode - userAgeGroup:', userAgeGroup, 'normalized:', normalizedAgeGroup);
  if (normalizedAgeGroup !== '13-and-under') {
    return null;
  }

  useEffect(() => {
    loadConnectionCode();
  }, [userId]);

  const loadConnectionCode = async () => {
    setLoading(true);
    try {
      // Use the new method that prevents unnecessary regeneration
      const code = await childConnectionService.getOrCreateChildCode(userId);
      if (code) {
        setConnectionCode(code);
      } else {
        toast({
          title: "Error",
          description: "Failed to load your connection code",
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error('Error loading connection code:', error);
      toast({
        title: "Error",
        description: "Failed to load your connection code",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCopyCode = async () => {
    try {
      // Try modern clipboard API first
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(connectionCode);
        toast({
          title: "Code Copied!",
          description: "Your connection code has been copied to clipboard",
        });
      } else {
        // Fallback for older browsers or non-secure contexts
        const textArea = document.createElement('textarea');
        textArea.value = connectionCode;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        
        try {
          const successful = document.execCommand('copy');
          document.body.removeChild(textArea);
          
          if (successful) {
            toast({
              title: "Code Copied!",
              description: "Your connection code has been copied to clipboard",
            });
          } else {
            throw new Error('Copy command failed');
          }
        } catch (fallbackError) {
          document.body.removeChild(textArea);
          throw fallbackError;
        }
      }
    } catch (error) {
      toast({
        title: "Copy Failed",
        description: "Unable to copy code. Please select and copy manually.",
        variant: "destructive"
      });
    }
  };

  const handleRegenerateCode = async () => {
    setRegenerating(true);
    try {
      const newCode = await childConnectionService.generateNewConnectionCode(userId);
      if (newCode) {
        setConnectionCode(newCode);
        toast({
          title: "New Code Generated",
          description: "Your connection code has been updated",
        });
      } else {
        toast({
          title: "Error",
          description: "Failed to generate new code",
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error('Error regenerating code:', error);
      toast({
        title: "Error",
        description: "Failed to generate new code",
        variant: "destructive"
      });
    } finally {
      setRegenerating(false);
    }
  };

  if (loading) {
    return (
      <Card className="bg-gradient-to-br from-primary/10 to-accent/10 border-primary/20">
        <CardContent className="p-6">
          <div className="animate-pulse">
            <div className="h-4 bg-muted rounded w-3/4 mb-4"></div>
            <div className="h-8 bg-muted rounded w-1/2"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-gradient-to-br from-primary/10 to-accent/10 border-primary/20">
      <CardHeader className="pb-3">
        <CardTitle className="text-primary flex items-center gap-2">
          <Users className="h-5 w-5" />
          My Connection Code
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-muted-foreground text-sm">
          Share this code with your parent or guardian so they can connect to your account and see your teams and activities.
        </p>
        
        <div className="bg-background/50 rounded-lg p-4 border">
          <div className="text-center">
            <div className="text-3xl font-mono font-bold tracking-widest text-primary mb-2">
              {connectionCode || 'Loading...'}
            </div>
            <p className="text-xs text-muted-foreground">
              Code expires in 30 days
            </p>
          </div>
        </div>

        <div className="flex gap-2">
          <Button
            onClick={handleCopyCode}
            disabled={!connectionCode}
            className="flex-1"
            variant="outline"
          >
            <Copy className="h-4 w-4 mr-2" />
            Copy Code
          </Button>
          
          <Button
            onClick={handleRegenerateCode}
            disabled={regenerating || !connectionCode}
            variant="outline"
            size="icon"
          >
            <RefreshCw className={`h-4 w-4 ${regenerating ? 'animate-spin' : ''}`} />
          </Button>
        </div>

        <div className="text-xs text-muted-foreground bg-muted/30 rounded p-3">
          <p className="font-medium mb-1">🔒 Safe & Secure</p>
          <p>Only your parent/guardian can use this code. It will automatically expire after 30 days for your safety.</p>
        </div>
      </CardContent>
    </Card>
  );
};

export default ChildConnectionCode;